import { ExtendedGridColDef } from 'types/datagrid';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { nestedValueGetter } from 'ui-component/DataGrid/ValueGetters';
import {
  getApplyFilterFnPart,
  getApplyFilterFnV2OrgParts,
  partV2SourceFilterOperators,
} from 'ui-component/DataGrid/FilterOperators';
import { ALL_APPS } from 'constants/appConstants';
import { renderPartV2Cell } from 'ui-component/DataGrid/Render/RenderPartV2Cell';
import { GridValueGetterParams } from '@mui/x-data-grid';
import { Stack, Tooltip } from '@mui/material';
import _ from 'lodash';
import {
  PartTypeFilterIds,
  PartTypeFilters,
} from 'views/parts/PartsV2/Parts/constants';
import { orgPartNoStockLotColumnDef } from 'ui-component/DataGrid/PartColumnDef';

export const V2OrgPartFieldName =
  'v2OrgPart.mpn|v2OrgPart.manufacturer|v2OrgPart.description|v2OrgPart.cofactrId|v2OrgPart.customId';
// Used to properly handle searching for CPID/cofactrId field on ServerSideDatagrids
// Currently, Flagship can't handle filtering by cofactrId, but part.id should be the same value
// Used when the part is an object with a CPID for the id
export const V2OrgPartFieldNameWithoutCofactrIdPartObject =
  'v2OrgPart.mpn|v2OrgPart.manufacturer|v2OrgPart.description|v2OrgPart.customId|part.id';
// Same as V2OrgPartFieldNameWithoutCofactrIdPartObject, but used when part is a CPID string
export const V2OrgPartFieldNameWithoutCofactrIdPartCPID =
  'v2OrgPart.mpn|v2OrgPart.manufacturer|v2OrgPart.description|v2OrgPart.customId|part';
export const V2OrgPartFieldNameWithDescription = `${V2OrgPartFieldName}|description`;
export const V2OrgPartsFieldName =
  'v2OrgParts.mpn|v2OrgParts.manufacturer|v2OrgParts.description|v2OrgParts.cofactrId|v2OrgParts.customId';

export const v2OrgPartColumnDef: ExtendedGridColDef = {
  field: V2OrgPartFieldName,
  headerName: 'Part',
  description: 'Manufacturer and part number',
  type: 'string',
  minWidth: 250,
  flex: 1,
  hideable: false,
  renderCell: (params: GridRenderCellParams<string>) =>
    renderPartV2Cell({
      orgPart: params.row.v2OrgPart,
    }),
  valueGetter: (params: GridValueGetterParams) => params.row.v2OrgPart?.mpn,
  getApplyQuickFilterFn: getApplyFilterFnV2OrgParts,
  permissionScope: {
    app: ALL_APPS.PARTS.id,
  },
};

export const orgPartV2ColumnDef: ExtendedGridColDef = {
  field: 'orgPart',
  headerName: 'Part',
  description: 'Manufacturer and part number',
  type: 'string',
  minWidth: 250,
  flex: 1,
  renderCell: (params: GridRenderCellParams<string>) =>
    renderPartV2Cell({
      orgPart: params.row.orgPart,
      stockLot: 'stockLot' in params.row ? params.row.stockLot : undefined,
    }),
  valueGetter: nestedValueGetter,
  getApplyQuickFilterFn: getApplyFilterFnPart,
  permissionScope: {
    app: ALL_APPS.PARTS.id,
  },
};

export const publicPartColumnDef: ExtendedGridColDef = {
  field: 'publicPart',
  headerName: 'Part',
  description: 'Manufacturer and part number',
  type: 'string',
  minWidth: 250,
  flex: 1,
  renderCell: (params: GridRenderCellParams<string>) =>
    renderPartV2Cell({
      publicPart: params.row,
      stockLot: 'stockLot' in params.row ? params.row.stockLot : undefined,
    }),
  valueGetter: nestedValueGetter,
  getApplyQuickFilterFn: getApplyFilterFnPart,
  permissionScope: {
    app: ALL_APPS.PARTS.id,
  },
};

export const partSourceColumnDef: ExtendedGridColDef = {
  field: 'publicPart',
  headerName: 'Source',
  type: 'singleSelect',
  minWidth: 75,
  flex: 0.3,
  sortable: false,
  valueGetter: (params: GridValueGetterParams) =>
    params.row.source
      ? PartTypeFilters[params.row.source as PartTypeFilterIds]?.label
      : '',
  filterOperators: partV2SourceFilterOperators,
  renderCell: (params: GridRenderCellParams<string>) => {
    const sourceType = PartTypeFilters[params.row.source as PartTypeFilterIds];
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-start"
      >
        {_.map(sourceType?.icons, (icon) => {
          const Icon = icon?.Icon;
          return (
            <Tooltip key={icon.id} title={icon.label}>
              {/* Span added to properly handle rendering tooltip for custom svg Icon */}
              <span>
                <Icon />
              </span>
            </Tooltip>
          );
        })}
      </Stack>
    );
  },
};

export const orgPartNoStockLotColumnDefV2OrgPart: ExtendedGridColDef = {
  ...orgPartNoStockLotColumnDef,
  field: V2OrgPartFieldName,
  renderCell: (params: GridRenderCellParams<string>) =>
    renderPartV2Cell({
      orgPart: params.row.v2OrgPart,
    }),
  valueGetter: (params: GridValueGetterParams) => params.row.v2OrgPart?.mpn,
  getApplyQuickFilterFn: getApplyFilterFnV2OrgParts,
  permissionScope: {
    app: ALL_APPS.PARTS.id,
  },
};
